import React, { useState, useEffect } from "react";
import { useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Star, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";

const Reviews = () => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`, { withCredentials: true });
      setReviews(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const togglePublic = async (reviewId, isPublic) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}`, 
        { is_public: !isPublic },
        { withCredentials: true }
      );
      toast.success(language === "it" ? "Visibilità aggiornata" : "Visibility updated");
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t("reviews")}</h1>
          <p className="text-muted-foreground mt-1">
            {language === "it" ? "Gestisci le recensioni dei clienti" : "Manage client reviews"}
          </p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
              </div>
              <div>
                <p className="text-3xl font-mono font-bold">{averageRating}</p>
                <p className="text-sm text-muted-foreground">{language === "it" ? "Media recensioni" : "Average rating"}</p>
              </div>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <span className="font-mono font-bold text-accent">{reviews.length}</span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{language === "it" ? "Totale recensioni" : "Total reviews"}</p>
              </div>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Eye className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-3xl font-mono font-bold">{reviews.filter(r => r.is_public).length}</p>
                <p className="text-sm text-muted-foreground">{language === "it" ? "Pubbliche" : "Public"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-4">
          {reviews.map((review) => (
            <div 
              key={review.review_id}
              className="p-5 rounded-2xl border bg-card shadow-card"
              data-testid={`review-${review.review_id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i}
                        className={`w-5 h-5 ${
                          i < review.rating 
                            ? "text-amber-500 fill-amber-500" 
                            : "text-muted-foreground"
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-muted-foreground">
                      {format(new Date(review.created_at), "PPP", { 
                        locale: language === "it" ? it : enUS 
                      })}
                    </span>
                  </div>
                  
                  {review.comment && (
                    <p className="text-muted-foreground">{review.comment}</p>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => togglePublic(review.review_id, review.is_public)}
                  className={review.is_public ? "text-emerald-600" : "text-muted-foreground"}
                  data-testid={`toggle-public-${review.review_id}`}
                >
                  {review.is_public ? (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      {language === "it" ? "Pubblica" : "Public"}
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" />
                      {language === "it" ? "Nascosta" : "Hidden"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {reviews.length === 0 && (
          <div className="text-center py-12">
            <Star className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">
              {language === "it" ? "Nessuna recensione ancora" : "No reviews yet"}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Reviews;
